import React, { useState, useEffect } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebaseConfig';
import { translations, stateDistrictData } from './data';
import PrintSummary from './PrintSummary';
import LoadingSpinner from './components/LoadingSpinner';
import OfflineIndicator from './components/OfflineIndicator';
// import { getAnalytics, logEvent } from 'firebase/analytics';
// import { analytics } from './firebaseConfig';

const App = () => {
  // Language state
  const [language, setLanguage] = useState('en');
  const t = translations[language];

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    mobileNumber: '',
    isWhatsApp: false,
    gender: '',
    age: '',
    occupation: '',
    state: '',
    district: ''
  });

  // Validation errors state
  const [errors, setErrors] = useState({});
  
  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Save language preference to localStorage
  useEffect(() => {
    const savedLanguage = localStorage.getItem('preferredLanguage');
    if (savedLanguage) {
      setLanguage(savedLanguage);
    }
  }, []);

  const toggleLanguage = () => {
    const newLanguage = language === 'en' ? 'hi' : 'en';
    setLanguage(newLanguage);
    localStorage.setItem('preferredLanguage', newLanguage);
  };

  // Get available districts based on selected state
  const getDistricts = () => {
    if (!formData.state || !stateDistrictData[formData.state]) return [];
    return stateDistrictData[formData.state].districts[language];
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear district when state changes
    if (name === 'state') {
      setFormData(prev => ({ ...prev, district: '' }));
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t.errors.nameRequired;
    }

    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = t.errors.mobileRequired;
    } else if (!/^\d{10}$/.test(formData.mobileNumber)) {
      newErrors.mobileNumber = t.invalidMobile;
    }

    if (!formData.gender) {
      newErrors.gender = t.errors.genderRequired;
    }

    if (!formData.age) {
      newErrors.age = t.errors.ageRequired;
    } else if (formData.age < 18 || formData.age > 100) {
      newErrors.age = t.invalidAge;
    }

    if (!formData.occupation) {
      newErrors.occupation = t.errors.occupationRequired;
    }

    if (!formData.state) {
      newErrors.state = t.errors.stateRequired;
    }

    if (!formData.district) {
      newErrors.district = t.errors.districtRequired;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Save to Firestore with server timestamp
      const docRef = await addDoc(collection(db, 'registrations'), {
        ...formData,
        createdAt: serverTimestamp(),
        submittedAt: new Date().toISOString()
      });

      console.log('Document written with ID: ', docRef.id);
      
      // Log analytics event (uncomment to use)
      // logEvent(analytics, 'form_submission', {
      //   form_name: 'registration'
      // });

      // Show success message and summary
      setShowSummary(true);

    } catch (error) {
      console.error('Error adding document: ', error);
      setSubmitError('Error submitting form. Please try again.');
      setTimeout(() => setSubmitError(''), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle new registration
  const handleNewRegistration = () => {
    setFormData({
      name: '',
      mobileNumber: '',
      isWhatsApp: false,
      gender: '',
      age: '',
      occupation: '',
      state: '',
      district: ''
    });
    setErrors({});
    setShowSummary(false);
  };

  // If showing summary, render PrintSummary component
  if (showSummary) {
    return (
      <PrintSummary
        formData={formData}
        language={language}
        onNewRegistration={handleNewRegistration}
      />
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {/* Header with Language Toggle */}
      <div className="bg-white shadow-sm flex-shrink-0">
        <div className="max-w-md mx-auto px-4 py-2 flex justify-between items-center">
          <h1 className="text-lg font-bold text-gray-800">
            {t.title}
          </h1>
          <button
            type="button"
            onClick={toggleLanguage}
            className="bg-indigo-600 text-white px-3 py-1.5 text-sm rounded-full hover:bg-indigo-700 transition duration-200 shadow-md"
          >
            {language === 'en' ? 'हिन्दी' : 'English'}
          </button>
        </div>
      </div>

      {/* Main Content - Scrollable */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-md mx-auto px-4 py-4">
          <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name Field */}
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-1">
                    {t.name} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 sm:px-4 sm:py-3 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                      errors.name 
                        ? 'border-red-500 focus:ring-red-500' 
                        : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-500 hover:border-gray-300'
                    }`}
                    placeholder={language === 'en' ? 'Enter your full name' : 'अपना पूरा नाम लिखें'}
                  />
                  {errors.name && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.name}
                    </p>
                  )}
                </div>

                {/* Mobile Number Field */}
                <div>
                  <label htmlFor="mobileNumber" className="block text-sm font-semibold text-gray-700 mb-1">
                    {t.mobileNumber} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="mobileNumber"
                    name="mobileNumber"
                    value={formData.mobileNumber}
                    onChange={handleInputChange}
                    maxLength="10"
                    className={`w-full px-3 py-2 sm:px-4 sm:py-3 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                      errors.mobileNumber 
                        ? 'border-red-500 focus:ring-red-500' 
                        : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-500 hover:border-gray-300'
                    }`}
                    placeholder="9876543210"
                    inputMode="numeric"
                    pattern="[0-9]*"
                  />
                  {errors.mobileNumber && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.mobileNumber}
                    </p>
                  )}
                </div>

                {/* WhatsApp Toggle */}
                <div className="border-2 border-gray-200 rounded-lg p-3 hover:border-gray-300 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <label htmlFor="isWhatsApp" className="text-sm font-semibold text-gray-700 block">
                        {t.whatsappQuestion}
                      </label>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {formData.isWhatsApp 
                          ? (language === 'en' ? 'We\'ll contact you on WhatsApp' : 'हम आपसे WhatsApp पर संपर्क करेंगे') 
                          : (language === 'en' ? 'WhatsApp number is different' : 'WhatsApp नंबर अलग है')}
                      </p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={formData.isWhatsApp}
                      onClick={() => handleInputChange({ target: { name: 'isWhatsApp', type: 'checkbox', checked: !formData.isWhatsApp } })}
                      className={`relative inline-flex h-8 w-14 flex-shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
                        formData.isWhatsApp ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        aria-hidden="true"
                        className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform duration-300 ease-in-out ${
                          formData.isWhatsApp ? 'translate-x-7' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Gender Field */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t.gender} <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {['female', 'male', 'other'].map((gender) => (
                      <label key={gender} className={`relative flex items-center justify-center p-2 sm:p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                        formData.gender === gender
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}>
                        <input
                          type="radio"
                          name="gender"
                          value={gender}
                          checked={formData.gender === gender}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <span className="text-xs sm:text-sm font-medium text-center">
                          {gender === 'male' ? t.male : gender === 'female' ? t.female : t.other}
                        </span>
                        {formData.gender === gender && (
                          <svg className="absolute -top-1 -right-1 w-4 h-4 text-indigo-600 bg-white rounded-full" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        )}
                      </label>
                    ))}
                  </div>
                  {errors.gender && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.gender}
                    </p>
                  )}
                </div>

                {/* Age Field */}
                <div>
                  <label htmlFor="age" className="block text-sm font-semibold text-gray-700 mb-1">
                    {t.age} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    id="age"
                    name="age"
                    value={formData.age}
                    onChange={handleInputChange}
                    min="18"
                    max="100"
                    className={`w-full px-3 py-2 sm:px-4 sm:py-3 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                      errors.age 
                        ? 'border-red-500 focus:ring-red-500' 
                        : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-500 hover:border-gray-300'
                    }`}
                    placeholder={language === 'en' ? '25' : '२५'}
                    inputMode="numeric"
                  />
                  {errors.age && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.age}
                    </p>
                  )}
                </div>

                {/* Occupation Field */}
                <div>
                  <label htmlFor="occupation" className="block text-sm font-semibold text-gray-700 mb-1">
                    {t.occupation} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      id="occupation"
                      name="occupation"
                      value={formData.occupation}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 sm:px-4 sm:py-3 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 appearance-none ${
                        errors.occupation 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-500 hover:border-gray-300'
                      }`}
                    >
                      <option value="">{t.selectOccupation}</option>
                      <option value="business">{t.business}</option>
                      <option value="privateJob">{t.privateJob}</option>
                      <option value="governmentJob">{t.governmentJob}</option>
                      <option value="selfEmployed">{t.selfEmployed}</option>
                    </select>
                    <svg className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  {errors.occupation && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.occupation}
                    </p>
                  )}
                </div>

                {/* State Field */}
                <div>
                  <label htmlFor="state" className="block text-sm font-semibold text-gray-700 mb-1">
                    {t.state} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 sm:px-4 sm:py-3 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 appearance-none ${
                        errors.state 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-500 hover:border-gray-300'
                      }`}
                    >
                      <option value="">{t.selectState}</option>
                      {Object.keys(stateDistrictData).map((state) => (
                        <option key={state} value={state}>
                          {language === 'hi' ? stateDistrictData[state].hi : state}
                        </option>
                      ))}
                    </select>
                    <svg className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  {errors.state && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.state}
                    </p>
                  )}
                </div>

                {/* District Field */}
                <div>
                  <label htmlFor="district" className="block text-sm font-semibold text-gray-700 mb-1">
                    {t.district} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      id="district"
                      name="district"
                      value={formData.district}
                      onChange={handleInputChange}
                      disabled={!formData.state}
                      className={`w-full px-3 py-2 sm:px-4 sm:py-3 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 appearance-none ${
                        errors.district 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-500 hover:border-gray-300'
                      } ${!formData.state ? 'bg-gray-50 cursor-not-allowed opacity-60' : ''}`}
                    >
                      <option value="">{t.selectDistrict}</option>
                      {getDistricts().map((district, index) => (
                        <option 
                          key={district} 
                          value={stateDistrictData[formData.state]?.districts.en[index]}
                        >
                          {district}
                        </option>
                      ))}
                    </select>
                    <svg className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  {!formData.state && (
                    <p className="mt-1 text-xs text-gray-500">
                      {language === 'en' ? 'Select state first' : 'पहले राज्य चुनें'}
                    </p>
                  )}
                  {errors.district && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.district}
                    </p>
                  )}
                </div>

                {/* Error Message */}
                {submitError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center">
                    <svg className="w-4 h-4 text-red-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <p className="text-red-700 text-xs sm:text-sm font-medium">{submitError}</p>
                  </div>
                )}

                {/* Submit Button */}
                <div className="pt-1">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full py-3 px-4 rounded-lg text-white font-semibold text-base transition-all duration-200 flex items-center justify-center shadow-lg ${
                      isSubmitting
                        ? 'bg-gray-400 cursor-not-allowed opacity-70'
                        : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 hover:shadow-xl active:scale-[0.98]'
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <LoadingSpinner />
                        <span className="ml-2 text-sm sm:text-base">
                          {language === 'en' ? 'Submitting...' : 'जमा कर रहे हैं...'}
                        </span>
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-sm sm:text-base">{t.submit}</span>
                      </>
                    )}
                  </button>
                </div>
            </form>
          </div>
        </div>
        
        <OfflineIndicator language={language} />
      </div>
    </div>
  );
};

export default App;